import { useState } from 'react';

import { useSelector } from 'react-redux';

import { PAYMENT_HANDLER } from '@/config';
import { PLAN_TYPE } from '@/const/plans';
import { useShallowEqualSelector } from '@/hooks';
import { PAYMENT_METHOD_TYPE } from '@/models/payment';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import { ROLES } from '@/pages/Collaborators/CollaboratosModal/constants';
import taxInformationSchema from '@/store/billing/schema';

export function useIsCollaborator() {
	return useSelector(state => state.profile.user?.role === ROLES.collaborator);
}

export function useIsExtendedCollaborator() {
	return useSelector(
		state => state.profile.user?.role === ROLES.extendedCollaborator
	);
}

export function useIsOwner() {
	return !useSelector(state => state.profile?.user?.parentAccount);
}

export function useHasAccess() {
	const isOwner = useIsOwner();
	const { role, views } = useShallowEqualSelector(state => ({
		role: state.profile.user?.role,
		views: state.profile.user?.views
	}));

	return function hasAccess(view) {
		if (isOwner || role === ROLES.admin) {
			return true;
		}

		return views && views[view];
	};
}

export function useLanguage() {
	return useSelector(state => state.profile.language);
}

export function usePaymentMethod() {
	const OPTIONS = ['PAYMENT_METHOD_FIRST_OPTION'];
	const DEFAULT_OPTION = OPTIONS[0];
	const [selectedOption, setSelectedOption] = useState(DEFAULT_OPTION);
	const { cardLast4, cardBrand, cardType, type, linkEmail, walletType } =
		useShallowEqualSelector(state => state.profile?.paymentMethod) || {};

	const hasPaymentMethodMap = {
		[PAYMENT_METHOD_TYPE.CARD]: !!cardLast4,
		[PAYMENT_METHOD_TYPE.PAYPAL]: true,
		[PAYMENT_METHOD_TYPE.LINK]: true
	};

	return {
		hasPaymentMethod: !!hasPaymentMethodMap[type],
		info: {
			cardLast4,
			cardBrand,
			cardType,
			type,
			linkEmail,
			walletType
		},
		OPTIONS,
		DEFAULT_OPTION,
		selectedOption,
		setSelectedOption
	};
}

export function useTaxInformation() {
	const data =
		useShallowEqualSelector(state => ({
			email: state.profile.user?.email,
			...state.profile.user?.taxInformation
		})) || {};

	return {
		hasContent: !!Object.keys(data).length,
		isValid: taxInformationSchema({ sync: true }).isValidSync(data),
		data
	};
}

export function useShowCurrencySelector() {
	if (PAYMENT_HANDLER === 'adyen') {
		return true;
	}

	const subscriptionStatus = useSelector(
		state => state.profile.user?.subscription?.status
	);

	return (
		!subscriptionStatus ||
		[
			SUBSCRIPTION_STATUS.CANCELED,
			SUBSCRIPTION_STATUS.TRIALING,
			SUBSCRIPTION_STATUS.TRIAL_ENDED
		].includes(subscriptionStatus)
	);
}

export function useSubscribed() {
	const subscriptionStatus = useSelector(
		state => state.profile.user?.subscription?.status
	);
	return [SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIALING].includes(
		subscriptionStatus
	);
}

export function useUser() {
	const user = useSelector(state => state.profile.user);

	return user;
}

export function useSubscriptionInfo() {
	const subscriptionInfo = useSelector(
		state => state.profile.subscriptionInfo
	);

	const { status, plan, cancellationRequestAt } = subscriptionInfo || {};

	const isTrial =
		plan === PLAN_TYPE.TRIAL || status === SUBSCRIPTION_STATUS.TRIALING;

	let canUseTrial = true;

	if (typeof subscriptionInfo?.canUseTrial !== 'undefined') {
		canUseTrial = subscriptionInfo.canUseTrial;
	}

	return {
		...subscriptionInfo,
		cancellationRequested: !!cancellationRequestAt,
		status,
		plan,
		parsedPlan: isTrial ? 'trial' : plan,
		isTrial,
		isExpired: status === SUBSCRIPTION_STATUS.EXPIRED,
		canUseTrial,
		isCanceled: status === SUBSCRIPTION_STATUS.CANCELED,
		isPending: status === SUBSCRIPTION_STATUS.PENDING
	};
}

export function useHasActiveSubscription() {
	return useSelector(
		state =>
			state.profile?.user?.subscription?.status ===
			SUBSCRIPTION_STATUS.ACTIVE
	);
}

export function useHasBeenSubscribed() {
	return useSelector(state => state.profile?.user?.hasBeenSubscribed);
}

export function useHasStorageCharge() {
	return useSelector(state => state.profile?.user?.hasStorageCharge);
}

export function useIsTrialing() {
	return useSelector(
		state =>
			state.profile?.user?.subscription?.status ===
			SUBSCRIPTION_STATUS.TRIALING
	);
}

export const FEATURE_NAMES = {
	DELETE: 'delete',
	DOWNLOAD: 'download',
	PRINT: 'print',
	SAVE: 'save',
	SEND: 'send',
	SHARE: 'share'
};

export function useIsFeatureBlocked() {
	const isActive = useHasActiveSubscription();
	const isSubscribed = useSubscribed();

	return {
		[FEATURE_NAMES.DELETE]: false,
		[FEATURE_NAMES.DOWNLOAD]: false,
		[FEATURE_NAMES.PRINT]: !isSubscribed,
		[FEATURE_NAMES.SAVE]: !isActive,
		[FEATURE_NAMES.SEND]: !isSubscribed,
		[FEATURE_NAMES.SHARE]: !isSubscribed
	};
}

export function useTrialDiscount() {
	return useSelector(state => state.profile?.user?.trialDiscount);
}
